import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useDirectory from "../hooks/useDirectory";
import api from "../lib/api";
import { pilotFiltersFromState } from "../lib/dashboard";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { HEALTH_LABELS, LIST_HEALTH_CHOICES, LIST_STATUS_TABS, buildListParams, mergePages, pilotErrorMessage, pilotPath } from "../lib/pilots";
import { PilotMobileRow, PilotTableRow } from "../components/PilotRow";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw, Search, ShieldCheck, SlidersHorizontal } from "lucide-react";

const ALL = "__all__";
const ME = "__me__";
const PAGE_SIZE = 30;

// Filters, order and paging are the server's: this page sends the filters, shows pages in the order received and never
// re-sorts them (a local sort would pass for a global ranking, which a paged list cannot give).
export default function Pilots() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // The tableau de bord may land here with { filters } (lib/dashboard.js: « mine » and one health, nothing else), read once.
  const location = useLocation();
  const [preset] = useState(() => pilotFiltersFromState(location.state));
  const [status, setStatus] = useState("running");
  const [health, setHealth] = useState(preset?.health || "");
  const [responsible, setResponsible] = useState(preset?.responsible === "me" ? ME : "");  // "" | ME | a user id
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState("loading");  // loading | ready | error
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  const canListUsers = hasPermission(user, "prospects:assign") || hasPermission(user, "tasks:scope_all") || hasPermission(user, "tasks:edit");
  const directory = useDirectory(canListUsers);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // "Mes pilotes" = the pilots whose TEAM I am in (principal or intervenant); a person picked in the list = the pilots they are principal of.
  const mine = responsible === ME;
  const params = useMemo(
    () => buildListParams({ status, health, search, responsible: mine ? "" : responsible, member: mine ? user?.id : "", limit: PAGE_SIZE }),
    [status, health, search, responsible, mine, user?.id],
  );

  // Every filter change restarts from the first page. A response that arrives after a newer request is dropped.
  const loadFirst = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!silent) setPhase("loading");
    setLoadingMore(false);
    try {
      const { data } = await api.get("/pilots", { params });
      if (mine !== seq.current) return;
      setItems(data.items);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(pilotErrorMessage(e, "Impossible de charger les pilotes."));
      setPhase("error");
    }
  }, [params]);
  useEffect(() => { loadFirst(); return () => { seq.current += 1; }; }, [loadFirst]);

  const loadMore = async () => {
    if (loadingMore || !cursor) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/pilots", { params: { ...params, cursor } });
      if (mine !== seq.current) return;
      setItems((prev) => mergePages(prev, data.items));
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch (e) {
      if (mine === seq.current) toast.error(pilotErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const filtered = status !== "running" || !!health || !!search.trim() || !!responsible;
  const limitedOnly = items.length > 0 && items.every((i) => i.access === "limited");
  const canFilterResponsible = !limitedOnly;
  const reset = () => { setStatus("running"); setHealth(""); setResponsible(""); setSearchInput(""); setSearch(""); };

  const healthSelect = (
    <Select value={health || ALL} onValueChange={(v) => setHealth(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 w-full md:w-48" data-testid="pilots-health-filter" aria-label="Santé"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Toutes les santés</SelectItem>
        {LIST_HEALTH_CHOICES.map((h) => <SelectItem key={h} value={h}>{HEALTH_LABELS[h]}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  const responsibleSelect = !canFilterResponsible ? null : directory.length > 0 ? (
    <Select value={responsible || ALL} onValueChange={(v) => setResponsible(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 w-full md:w-48" data-testid="pilots-responsible-filter" aria-label="Principal"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Tous les principaux</SelectItem>
        <SelectItem value={ME}>Mes pilotes</SelectItem>
        {directory.filter((u) => u.id !== user?.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
  ) : (
    <Button variant={responsible === ME ? "default" : "outline"} size="sm" className={`h-9 ${responsible === ME ? "" : ""}`}
      aria-pressed={responsible === ME} onClick={() => setResponsible(responsible === ME ? "" : ME)} data-testid="pilots-mine-toggle">Mes pilotes</Button>
  );
  const activeFilterCount = (health ? 1 : 0) + (responsible ? 1 : 0);

  return (
    <div className="space-y-4" data-testid="pilots-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Pilotes</h1>
        <p className="text-sm text-slate-500">Suivez les pharmacies qui démarrent avec Jamora et repérez celles qui demandent votre attention.</p>
      </div>

      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Statut du pilote" data-testid="pilots-status-tabs">
        {LIST_STATUS_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={status === t.id} onClick={() => setStatus(t.id)} data-testid={`pilots-tab-${t.id}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${status === t.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0 md:max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Rechercher une pharmacie…" className="pl-9 h-9" aria-label="Rechercher un pilote" data-testid="pilots-search" />
        </div>
        <div className="hidden md:flex items-center gap-2">{healthSelect}{responsibleSelect}</div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="md:hidden h-9 gap-1.5 shrink-0" data-testid="pilots-filters-toggle" aria-label="Filtres">
              <SlidersHorizontal size={14} />{activeFilterCount > 0 && <span className="text-xs font-semibold">{activeFilterCount}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-3 md:hidden">{healthSelect}{responsibleSelect}</PopoverContent>
        </Popover>
      </div>

      {phase === "loading" && (
        <div className="space-y-2" data-testid="pilots-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
      )}

      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="pilots-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={() => loadFirst()} data-testid="pilots-retry"><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="pilots-empty">
          <ShieldCheck className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucun pilote ne correspond</h3>
              <p className="text-sm text-slate-500">Modifiez ou effacez les filtres pour élargir la liste.</p>
              <Button variant="outline" size="sm" onClick={reset} data-testid="pilots-reset">Effacer les filtres</Button>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">Aucun pilote en cours</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Un pilote se crée depuis la fiche d'une pharmacie gagnée. Il apparaîtra ici dès sa création.</p>
            </>
          )}
        </Card>
      )}

      {phase === "ready" && items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="pilots-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pharmacie</TableHead><TableHead>Statut</TableHead><TableHead>Santé</TableHead><TableHead>Principal</TableHead><TableHead>Équipe</TableHead><TableHead>Prochain suivi</TableHead>
                  <TableHead className="hidden xl:table-cell">Dernière activité</TableHead><TableHead className="hidden xl:table-cell">Autonomie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{items.map((i) => <PilotTableRow key={i.id} item={i} onOpen={(prospectId) => navigate(pilotPath(prospectId))} />)}</TableBody>
            </Table>
          </Card>
          <Card className="md:hidden overflow-hidden" data-testid="pilots-list-mobile">{items.map((i) => <PilotMobileRow key={i.id} item={i} />)}</Card>

          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="pilots-count">{items.length} pilote{items.length > 1 ? "s" : ""} affiché{items.length > 1 ? "s" : ""}{hasMore ? " (d'autres suivent)" : ""}</span>
            {hasMore && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="pilots-load-more">
                {loadingMore ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Charger plus
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
